import React, { useEffect, useMemo, useState } from 'react';
import Modal from './Modal.tsx';
import { useMembers } from '../hooks/useMembers.ts';
import { useCallRegister, useSaveCallRegister } from '../hooks/useCallRegister.ts';
import { useWorkflowSettings, FINE_TYPE_LABELS } from '../hooks/useWorkflowSettings.ts';

/* Roll call for one meeting. Each member is marked present, late, absent or excused; a fine is charged for everything except
   "present" using the group's fine amounts, and an admin can type a different amount for one member. */
const STATUSES = [
  ['present', 'Present', 'bg-green-600 text-white'],
  ['late', 'Late', 'bg-amber-500 text-white'],
  ['absent', 'Absent', 'bg-red-600 text-white'],
  ['excused', 'Apology', 'bg-blue-600 text-white'],
];
const DEFAULT_FINE = { present: null, late: 'late_comer', absent: 'absent', excused: 'absent_with_apology' };

export default function CallRegisterModal({ meeting, onClose }) {
  const { data: membersData } = useMembers();
  const { data: settings } = useWorkflowSettings();
  const { data: register } = useCallRegister(meeting?.id ?? null);
  const save = useSaveCallRegister(meeting?.id ?? null);
  const [rows, setRows] = useState({});
  const [search, setSearch] = useState('');

  const members = membersData?.data || [];
  const rates = register?.call_register_settings || settings?.call_register_fine_rates || {};

  // Start from what was recorded before, if anything.
  useEffect(() => {
    const next = {};
    members.forEach((m) => { next[m.id] = { status: 'present', violation_type: null, amount: '' }; });
    (register?.attendances || []).forEach((a) => {
      const status = ['present', 'late', 'absent', 'excused'].includes(a.status) ? a.status : 'present';
      next[a.member_id] = { status, violation_type: a.fine?.fine_type ?? DEFAULT_FINE[status], amount: a.fine?.amount ? String(Number(a.fine.amount)) : '' };
    });
    setRows(next);
  }, [membersData, register]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (id, patch) => setRows((r) => ({ ...r, [id]: { ...r[id], ...patch } }));
  const setStatus = (id, status) => set(id, { status, violation_type: DEFAULT_FINE[status], amount: '' });
  const typeOf = (row) => (row.status === 'present' ? row.violation_type : row.violation_type || DEFAULT_FINE[row.status]);
  const fineFor = (row) => { const t = typeOf(row); return !t ? 0 : row.amount !== '' ? Number(row.amount) : Number(rates[t] || 0); };

  const visible = useMemo(() => members.filter((m) => !search || m.name.toLowerCase().includes(search.toLowerCase())), [members, search]);
  const totals = useMemo(() => Object.values(rows).reduce((t, r) => ({ ...t, [r.status]: (t[r.status] || 0) + 1, fines: t.fines + fineFor(r) }), { fines: 0 }), [rows, rates]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = () => {
    const entries = members.map((m) => {
      const r = rows[m.id] || { status: 'present' };
      return {
        member_id: m.id,
        status: r.status,
        violation_type: typeOf(r) || null,
        amount: typeOf(r) && r.amount !== '' ? Number(r.amount) : null,
      };
    });
    save.mutate(entries, { onSuccess: onClose });
  };

  return (
    <Modal isOpen={!!meeting} onClose={onClose} title={`Call register: ${meeting?.title || ''}`} size="xl">
      <div data-testid="call-register">
        <p className="text-sm text-gray-600 mb-3">
          Present {totals.present || 0} · Late {totals.late || 0} · Absent {totals.absent || 0} · Apology {totals.excused || 0}
          {' '}· Fines <strong>KES {Number(totals.fines || 0).toLocaleString()}</strong>
        </p>
        {register?.call_register_completed_at && <p className="text-xs text-amber-700 bg-amber-50 rounded p-2 mb-3">A register was already saved for this meeting. Saving again updates attendance. Anyone already fined here keeps that fine (change it on the Fines page), so nobody is charged twice.</p>}
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search members" className="mb-3 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
        <ul className="divide-y divide-gray-100 max-h-[50vh] overflow-y-auto">
          {visible.map((m) => {
            const r = rows[m.id] || { status: 'present', amount: '' };
            return (
              <li key={m.id} className="py-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-medium text-gray-900">{m.name}</span>
                  <div className="flex gap-1" role="group" aria-label={`Status for ${m.name}`}>
                    {STATUSES.map(([key, label, on]) => (
                      <button key={key} type="button" onClick={() => setStatus(m.id, key)} aria-pressed={r.status === key}
                        className={`px-2.5 py-1 text-xs rounded-full ${r.status === key ? on : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>{label}</button>
                    ))}
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                  <select value={typeOf(r) || ''} onChange={(e) => set(m.id, { violation_type: e.target.value || null, amount: '' })} className="rounded border border-gray-300 px-2 py-1" aria-label={`Fine for ${m.name}`}>
                    {r.status === 'present' && <option value="">No fine</option>}
                    {Object.keys(FINE_TYPE_LABELS).map((k) => <option key={k} value={k}>{FINE_TYPE_LABELS[k]}</option>)}
                  </select>
                  {typeOf(r) && (
                    <label className="flex items-center gap-1">Fine KES
                      <input type="number" min="0" value={r.amount} placeholder={String(rates[typeOf(r)] ?? 0)} onChange={(e) => set(m.id, { amount: e.target.value })} className="w-24 rounded border border-gray-300 px-2 py-1" />
                    </label>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
        <div className="flex justify-end gap-2 pt-4">
          <button onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md">Cancel</button>
          <button onClick={submit} disabled={save.isPending || members.length === 0} className="px-4 py-2 text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50">
            {save.isPending ? 'Saving...' : 'Save register'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
